/**
 * @fileoverview Admin Uploads Page
 *
 * Centralized upload center for project documents, progress photos, and
 * selection documents. A top-level project selector pre-fills all three
 * upload modals; leaving it blank lets each modal pick its own project.
 * Below the upload cards, a Recent Uploads feed shows the last 10 files
 * across all projects (sourced from Cloudinary via useRecentUploads),
 * with a list/card view toggle.
 *
 * @component
 * @requires react
 * @requires @fortawesome/react-fontawesome
 * @requires ../../hooks/useAdminProjects
 * @requires ../../hooks/useRecentUploads
 * @version 1.1.0
 */

import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSwatchbook,
  faCloudArrowUp,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFile,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import {
  FaSpinner,
  FaExclamationTriangle,
  FaTh,
  FaList,
  FaUpload,
} from "react-icons/fa";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import PlansDocsUploadModal from "../../components/admin/PlansDocsUploadModal";
import ProgressPhotoUploadModal from "../../components/admin/ProgressPhotoUploadModal";
import SelectionDocsUploadModal from "../../components/admin/SelectionDocsUploadModal";
import { useAdminProjects } from "../../hooks/useAdminProjects";
import { useRecentUploads } from "../../hooks/useRecentUploads";
import { getTimeAgoET } from "../../utils/dateUtils";
import "./AdminUploads.css";

/* ============================================
   CONSTANTS
   ============================================ */

/** @constant Modal type keys used to track which upload modal is open. */
const MODAL_TYPES = {
  PROJECT_DOCS: "projectDocs",
  PROGRESS_PHOTOS: "progressPhotos",
  SELECTION_DOCS: "selectionDocs",
};

/** @constant Recent uploads view modes. */
const VIEW_MODES = {
  LIST: "list",
  CARD: "card",
};

/** @constant Image-format extensions used for thumbnail generation. */
const IMAGE_FORMATS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "heif",
  "svg",
]);

/**
 * Maps a `source_table` value (set at upload time) to a human-readable
 * prefix that scopes the category label. Used to build labels like
 * "Change Order Signed Documentation" or "Selection Docs Roofing & Gutters"
 * so the recent feed shows where each file lives, not just the leaf
 * category.
 *
 * Keep in sync with sourceTable strings used in the upload modals
 * (search the codebase for `sourceTable:` in `src/components/admin/`).
 *
 * @constant {Object<string, string>}
 */
const SOURCE_TABLE_LABELS = {
  projects: "Project Docs:",
  custom_selections: "Selection Docs:",
  change_orders: "Change Order:",
  tasks: "",
  files_photos: "",
};

/* ============================================
   HELPERS
   ============================================ */

/**
 * Build the display filename for an upload. Cloudinary's
 * `original_filename` is the user's filename at upload time and already
 * contains the extension, so we must NOT re-append `format` (would yield
 * "photo.jpg.jpg"). Falls back to public_id if the original is missing.
 *
 * @param {Object} upload - Normalized upload record
 * @returns {string} Filename to render
 */
const getDisplayFilename = (upload) => {
  const orig = upload.original_filename;
  if (orig) return orig;

  const id = upload.public_id || "";
  const leaf = id.split("/").pop() || "Untitled";
  return upload.format ? `${leaf}.${upload.format}` : leaf;
};

/**
 * Build the combined category label, scoping the leaf category by its
 * source table. Returns the source label alone if no leaf category is
 * present, the leaf alone if no source mapping is known, or the empty
 * string if neither is available.
 *
 * @param {Object} upload - Normalized upload record
 * @returns {string} Combined "Source Category" label, or "" if unknown
 */
const getCombinedCategoryLabel = (upload) => {
  const source = SOURCE_TABLE_LABELS[upload.source_table] || "";
  const leaf = upload.category_label || "";

  if (source && leaf) return `${source} ${leaf}`;
  return source || leaf;
};

/**
 * Decide which inline thumbnail strategy to use for a given upload.
 * - "image"  → render a Cloudinary-resized <img>
 * - "pdf"    → render a Cloudinary first-page JPG <img>
 * - "icon"   → render a Font Awesome file-type icon
 *
 * @param {Object} upload - Normalized upload record
 * @returns {"image"|"pdf"|"icon"}
 */
const getThumbnailStrategy = (upload) => {
  const fmt = (upload.format || "").toLowerCase();
  const url = upload.secure_url || "";
  const cloudinaryImageUpload = url.includes("/image/upload/");

  if (cloudinaryImageUpload && IMAGE_FORMATS.has(fmt)) return "image";
  if (cloudinaryImageUpload && fmt === "pdf") return "pdf";
  return "icon";
};

/**
 * Insert a Cloudinary transformation segment into a `/image/upload/`
 * URL so the browser downloads a small, square thumbnail instead of the
 * full-resolution asset. For PDFs, also rasterizes page 1 to JPG.
 *
 * Returns the original URL unchanged if it is not a Cloudinary
 * `/image/upload/` URL (callers should fall back to an icon in that case).
 *
 * @param {Object} upload - Normalized upload record
 * @param {number} [size=120] - Square thumbnail edge length
 * @returns {string} Transformed Cloudinary URL
 */
const buildThumbnailUrl = (upload, size = 120) => {
  const url = upload.secure_url || "";
  if (!url.includes("/image/upload/")) return url;

  const fmt = (upload.format || "").toLowerCase();
  const isPdf = fmt === "pdf";

  const transform = isPdf
    ? `c_fill,w_${size},h_${size},pg_1,f_jpg,q_auto`
    : `c_fill,w_${size},h_${size},q_auto,f_auto`;

  return url.replace("/image/upload/", `/image/upload/${transform}/`);
};

/**
 * Pick a Font Awesome icon for non-thumbnailable file types.
 *
 * @param {string} format - Lowercase Cloudinary format value
 * @returns {Object} Font Awesome icon definition
 */
const getFileIcon = (format) => {
  const f = (format || "").toLowerCase();
  if (f === "pdf") return faFilePdf;
  if (f === "doc" || f === "docx") return faFileWord;
  if (f === "xls" || f === "xlsx" || f === "csv") return faFileExcel;
  return faFile;
};

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Renders an inline thumbnail or icon for an upload. Image and PDF
 * thumbnails fall back to the file-type icon if the network request
 * fails (e.g., raw uploads, blocked PDF page rendering).
 *
 * @param {Object} props
 * @param {Object} props.upload - Normalized upload record
 * @param {("sm"|"lg")} [props.size="sm"] - Visual size variant
 * @returns {JSX.Element}
 */
const UploadThumbnail = ({ upload, size = "sm" }) => {
  const [errored, setErrored] = useState(false);
  const strategy = getThumbnailStrategy(upload);
  const px = size === "lg" ? 240 : 96;

  if (errored || strategy === "icon") {
    return (
      <div className={`au-thumb au-thumb-${size} au-thumb-icon`}>
        <FontAwesomeIcon icon={getFileIcon(upload.format)} />
      </div>
    );
  }

  return (
    <div className={`au-thumb au-thumb-${size}`}>
      <img
        src={buildThumbnailUrl(upload, px)}
        alt={getDisplayFilename(upload)}
        loading="lazy"
        onError={() => setErrored(true)}
      />
    </div>
  );
};

/**
 * One of the three upload-type cards shown in the responsive grid.
 *
 * @param {Object} props
 * @param {Object} props.icon - Font Awesome icon definition
 * @param {string} props.iconClass - Color modifier class (au-icon-docs etc.)
 * @param {string} props.title - Card heading
 * @param {string} props.description - Short description under the heading
 * @param {Function} props.onClick - Handler for the Upload button
 * @returns {JSX.Element}
 */
const UploadTypeCard = ({ icon, iconClass, title, description, onClick }) => (
  <div className="au-upload-card">
    <div className={`au-upload-card-icon ${iconClass}`}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <h3 className="au-upload-card-title">{title}</h3>
    <p className="au-upload-card-desc">{description}</p>
    <button className="au-upload-card-btn" onClick={onClick}>
      <FontAwesomeIcon icon={faCloudArrowUp} />
      <span>Upload</span>
    </button>
  </div>
);

/**
 * Open an upload's secure URL in a new tab.
 * @param {Object} upload - Normalized upload record
 */
const openUpload = (upload) => {
  if (upload.secure_url) {
    window.open(upload.secure_url, "_blank", "noopener,noreferrer");
  }
};

/**
 * Single row in the desktop Recent Uploads table.
 *
 * @param {Object} props
 * @param {Object} props.upload - Normalized upload record from useRecentUploads
 * @returns {JSX.Element}
 */
const RecentUploadRow = ({ upload }) => {
  const category = getCombinedCategoryLabel(upload);
  return (
    <tr className="au-recent-row" onClick={() => openUpload(upload)}>
      <td className="au-recent-cell-thumb">
        <UploadThumbnail upload={upload} size="sm" />
      </td>
      <td>
        <span className="au-recent-filename">{getDisplayFilename(upload)}</span>
      </td>
      <td>
        <span className="au-recent-project">{upload.project_name || "—"}</span>
      </td>
      <td>
        {category && <span className="au-recent-category">{category}</span>}
      </td>
      <td>
        <span className="au-recent-uploader">
          {upload.uploaded_by_name || "—"}
        </span>
      </td>
      <td>
        <span className="au-recent-time">
          {getTimeAgoET(upload.created_at)}
        </span>
      </td>
    </tr>
  );
};

/**
 * Mobile / grid-view card representation of a single Recent Uploads entry.
 *
 * @param {Object} props
 * @param {Object} props.upload - Normalized upload record
 * @returns {JSX.Element}
 */
const RecentUploadCard = ({ upload }) => {
  const category = getCombinedCategoryLabel(upload);
  return (
    <div className="au-recent-card" onClick={() => openUpload(upload)}>
      <UploadThumbnail upload={upload} size="md" />
      <div className="au-recent-card-body">
        <span
          className="au-recent-card-name"
          title={getDisplayFilename(upload)}
        >
          {getDisplayFilename(upload)}
        </span>
        <span className="au-recent-card-meta">
          {upload.project_name || "—"}
        </span>
        {category && <span className="au-recent-card-meta">{category}</span>}
        <span className="au-recent-card-meta">
          by: {upload.uploaded_by_name || "—"}
        </span>
        <span className="au-recent-time">
          {getTimeAgoET(upload.created_at)}
        </span>
      </div>
    </div>
  );
};

/**
 * Skeleton placeholder rows shown while recent uploads are loading.
 *
 * @param {Object} props
 * @param {number} [props.count=5] - Number of skeleton rows to render
 * @returns {JSX.Element}
 */
const RecentUploadsSkeleton = ({ count = 5 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="au-skeleton-row">
        <td>
          <span className="au-skeleton au-skeleton-thumb" />
        </td>
        <td>
          <span className="au-skeleton au-skeleton-md" />
        </td>
        <td>
          <span className="au-skeleton au-skeleton-md" />
        </td>
        <td>
          <span className="au-skeleton au-skeleton-sm" />
        </td>
        <td>
          <span className="au-skeleton au-skeleton-sm" />
        </td>
        <td>
          <span className="au-skeleton au-skeleton-sm" />
        </td>
      </tr>
    ))}
  </>
);

/**
 * Compact list/card view toggle styled to match the Projects page
 * (`admin-projects-view-toggle`). Local class names mirror that pattern
 * so the existing CSS theming carries over without duplication.
 *
 * @param {Object} props
 * @param {string} props.viewMode - Current view mode ("list" | "card")
 * @param {Function} props.onChange - Setter receiving the new mode
 * @returns {JSX.Element}
 */
const ViewModeToggle = ({ viewMode, onChange }) => (
  <div className="admin-projects-view-toggle au-view-toggle">
    <button
      className={`admin-projects-view-btn ${
        viewMode === VIEW_MODES.CARD ? "active" : ""
      }`}
      onClick={() => onChange(VIEW_MODES.CARD)}
    >
      <FaTh /> Card View
    </button>
    <button
      className={`admin-projects-view-btn ${
        viewMode === VIEW_MODES.LIST ? "active" : ""
      }`}
      onClick={() => onChange(VIEW_MODES.LIST)}
    >
      <FaList /> List View
    </button>
  </div>
);

/**
 * Container for the Recent Uploads feed — handles loading, error, empty,
 * and populated states for the desktop list, card grid, and mobile cards.
 *
 * @param {Object} props
 * @param {Array<Object>} props.uploads - Normalized upload records
 * @param {boolean} props.isLoading
 * @param {boolean} props.isError
 * @param {string} props.viewMode - "list" or "card"
 * @param {Function} props.onChangeViewMode
 * @param {Function} props.onRetry - Refetch handler
 * @returns {JSX.Element}
 */
const RecentUploadsSection = ({
  uploads,
  isLoading,
  isError,
  viewMode,
  onChangeViewMode,
  onRetry,
}) => (
  <section className="au-recent-section">
    <header className="au-recent-header">
      <div className="au-recent-header-text">
        <h2 className="au-recent-title">Recent Uploads</h2>
        <span className="au-recent-subtitle">
          Last 10 across all projects by all users
        </span>
      </div>
      <span className="hide-sm">
        <ViewModeToggle viewMode={viewMode} onChange={onChangeViewMode} />
      </span>
    </header>

    {isError ? (
      <div className="au-recent-error">
        Couldn't load recent uploads.
        <button className="au-recent-error-retry" onClick={onRetry}>
          Retry
        </button>
      </div>
    ) : viewMode === VIEW_MODES.LIST ? (
      <>
        {/* Desktop list view */}
        <div className="au-recent-table-wrap">
          <table className="au-recent-table">
            <thead>
              <tr>
                <th className="au-recent-th-thumb">Preview</th>
                <th>File</th>
                <th>Project</th>
                <th>Category</th>
                <th>Uploaded By</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <RecentUploadsSkeleton />
              ) : uploads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="au-recent-empty">
                    No uploads yet
                  </td>
                </tr>
              ) : (
                uploads.map((u) => (
                  <RecentUploadRow key={u.public_id} upload={u} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card fallback when list view is selected */}
        <div className="au-recent-cards au-recent-cards-mobile">
          {isLoading ? (
            <div className="au-recent-empty">
              <FaSpinner className="spinner spinner-small" /> Loading...
            </div>
          ) : uploads.length === 0 ? (
            <div className="au-recent-empty">No uploads yet</div>
          ) : (
            uploads.map((u) => (
              <RecentUploadCard key={u.public_id} upload={u} />
            ))
          )}
        </div>
      </>
    ) : (
      /* Card grid view (desktop + mobile) */
      <div className="au-recent-grid">
        {isLoading ? (
          <div className="au-recent-empty">
            <FaSpinner className="spinner spinner-small" /> Loading...
          </div>
        ) : uploads.length === 0 ? (
          <div className="au-recent-empty">No uploads yet</div>
        ) : (
          uploads.map((u) => <RecentUploadCard key={u.public_id} upload={u} />)
        )}
      </div>
    )}
  </section>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * AdminUploads Page
 *
 * @param {Object} props
 * @param {Object} props.user - Firebase auth user
 * @param {Object} props.userData - Authenticated user record
 * @param {boolean} [props.showTestAccounts=false] - When true, includes test
 *   projects in the project selector (forwarded to upload modals as well)
 * @returns {JSX.Element}
 */
function AdminUploads({ user, userData, showTestAccounts = false }) {
  // Top-level project pre-fill state
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Modal open state
  const [openModal, setOpenModal] = useState(null);

  // Recent Uploads view mode (card default)
  const [recentViewMode, setRecentViewMode] = useState(VIEW_MODES.CARD);

  // Projects for the top selector — same source as AdminProjects
  const {
    data: projects,
    isLoading: projectsLoading,
    error: projectsError,
  } = useAdminProjects({
    status: "all",
    phase: "all",
    type: "all",
    showTestAccounts,
  });

  // Recent uploads feed
  const {
    data: recentUploads = [],
    isLoading: uploadsLoading,
    isError: uploadsError,
    refetch: refetchUploads,
  } = useRecentUploads(10);

  /** Sorted project list — alphabetical by projectName for fast scanning. */
  const sortedProjects = useMemo(() => {
    if (!projects) return [];
    return [...projects].sort((a, b) =>
      (a.projectName || "").localeCompare(b.projectName || ""),
    );
  }, [projects]);

  /** Resolve the selected project's display name for modal pre-fill. */
  const selectedProjectName = useMemo(() => {
    if (!selectedProjectId) return "";
    const match = sortedProjects.find((p) => p.id === selectedProjectId);
    return match?.projectName || "";
  }, [selectedProjectId, sortedProjects]);

  /**
   * Filter the recent uploads feed so Test Account projects are hidden
   * unless the IT-only "Test Mode" toggle is on. `useAdminProjects`
   * already respects `showTestAccounts` server-side, so its result set
   * is the canonical list of visible projects — any upload whose
   * `project_id` isn't in that set (and isn't blank/unassigned) is
   * assumed to belong to a hidden test project and is dropped.
   */
  const visibleProjectIds = useMemo(
    () => new Set(sortedProjects.map((p) => p.id)),
    [sortedProjects],
  );

  const visibleRecentUploads = useMemo(() => {
    if (showTestAccounts) return recentUploads;
    if (projectsLoading || !sortedProjects.length) return recentUploads;
    return recentUploads.filter((u) => {
      if (!u.project_id) return true;
      return visibleProjectIds.has(u.project_id);
    });
  }, [
    recentUploads,
    showTestAccounts,
    projectsLoading,
    sortedProjects.length,
    visibleProjectIds,
  ]);

  const handleOpenModal = (type) => setOpenModal(type);
  const handleCloseModal = () => setOpenModal(null);
  const handleClearProject = () => setSelectedProjectId("");

  // Common props applied to every upload modal so the top selector pre-fills
  const sharedModalProps = {
    projectId: selectedProjectId || undefined,
    projectName: selectedProjectName || undefined,
    showTestAccounts,
  };

  return (
    <div className="au-page">
      <AdminPageBanner
        title="Upload Center"
        subtitle="Add files, photos, and documents to any project"
      >
        <div className="apb-icon">
          <FaUpload />
        </div>
      </AdminPageBanner>
      <div className="au-upload-section">
        {/* ===== Top-level project selector ===== */}
        <div className="au-selector-row">
          <div className="au-selector-row-left">
            <h2 className="au-selector-label">Pre-fill project (optional)</h2>
            <p className="au-selector-hint">
              Choose a project to pre-fill all three uploaders below.
            </p>
          </div>
          <div className="au-selector-row-right">
            <div className="au-selector-input-row">
              <select
                className="au-selector-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={projectsLoading || !!projectsError}
              >
                <option value="">
                  {projectsLoading
                    ? "Loading projects..."
                    : projectsError
                      ? "Couldn't load projects"
                      : "No project selected (choose per upload)"}
                </option>
                {sortedProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.projectName || ""}
                  </option>
                ))}
              </select>
              {selectedProjectId && (
                <button
                  className="au-selector-clear"
                  onClick={handleClearProject}
                >
                  Clear
                </button>
              )}
            </div>
            {selectedProjectId && (
              <div className="au-selector-status">
                Pre-filling uploads for{" "}
                <strong>{selectedProjectName || "selected project"}</strong>
              </div>
            )}
            {projectsError && !projectsLoading && (
              <div className="au-selector-status">
                <FaExclamationTriangle /> Project list unavailable — modals will
                still let you pick a project.
              </div>
            )}
          </div>
        </div>

        {/* ===== Upload type cards ===== */}
        <div className="au-upload-grid">
          <UploadTypeCard
            icon={faFileLines}
            iconClass="au-icon-docs"
            title="Upload Project Documents"
            description="Floorplans, site plans, permits, contracts"
            onClick={() => handleOpenModal(MODAL_TYPES.PROJECT_DOCS)}
          />
          <UploadTypeCard
            icon={faImage}
            iconClass="au-icon-photos"
            title="Upload Progress Photos"
            description="Construction photos, optionally tied to tasks"
            onClick={() => handleOpenModal(MODAL_TYPES.PROGRESS_PHOTOS)}
          />
          <UploadTypeCard
            icon={faSwatchbook}
            iconClass="au-icon-selections"
            title="Upload Selection Documents"
            description="Quotes, invoices, spec sheets for client selections"
            onClick={() => handleOpenModal(MODAL_TYPES.SELECTION_DOCS)}
          />
        </div>
      </div>

      {/* ===== Recent uploads ===== */}
      <RecentUploadsSection
        uploads={visibleRecentUploads}
        isLoading={uploadsLoading}
        isError={uploadsError}
        viewMode={recentViewMode}
        onChangeViewMode={setRecentViewMode}
        onRetry={refetchUploads}
      />

      {/* ===== Modals ===== */}
      <PlansDocsUploadModal
        isOpen={openModal === MODAL_TYPES.PROJECT_DOCS}
        onClose={handleCloseModal}
        onUploadComplete={() => refetchUploads()}
        {...sharedModalProps}
      />
      <ProgressPhotoUploadModal
        isOpen={openModal === MODAL_TYPES.PROGRESS_PHOTOS}
        onClose={handleCloseModal}
        onUploadComplete={() => refetchUploads()}
        {...sharedModalProps}
      />
      <SelectionDocsUploadModal
        isOpen={openModal === MODAL_TYPES.SELECTION_DOCS}
        onClose={handleCloseModal}
        onUploadComplete={() => refetchUploads()}
        {...sharedModalProps}
      />
    </div>
  );
}

export default AdminUploads;
